import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Paper, Button, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Helmet } from 'react-helmet-async';

const faqs = [
    {
        category: 'Getting Started',
        questions: [
            {
                q: 'What is Sequence?',
                a: 'Sequence is a classic strategy board game that combines card playing with board game mechanics. Players take turns playing cards from their hand and placing chips on corresponding spaces on a 10 by 10 game board. The goal is to be the first player or team to form a specified number of sequences, which are rows of five connected chips in a horizontal, vertical, or diagonal line.'
            },
            {
                q: 'How do I create a new game?',
                a: 'Creating a game on Play Sequence is simple. Visit the homepage and click "New Game." You will be asked to enter a room name and select the number of players for your game. Once the game is created, you will receive a shareable link that you can send to your friends. When everyone has joined through the link, the host can start the game and teams will be assigned.'
            },
            {
                q: 'How do I invite friends to my game?',
                a: 'After creating a game, you will see a shareable link on the screen. Copy this link and send it to your friends through any messaging platform, email, or social media. When your friends open the link in their browser, they will automatically join your game room. No accounts or downloads are required for anyone to play.'
            },
            {
                q: 'Is Play Sequence free to use?',
                a: 'Yes, Play Sequence is completely free. There are no hidden charges, premium subscriptions, or in-app purchases. You can create and join games without limitations, and all features including video calls and chat are available at no cost.'
            },
            {
                q: 'Do I need to create an account to play?',
                a: 'No, you do not need to create an account. When you first visit the site, a unique player identifier is generated and stored locally in your browser. You simply enter your name when joining a game, and you are ready to play. This makes it quick and easy to get started without any registration process.'
            },
        ]
    },
    {
        category: 'Gameplay',
        questions: [
            {
                q: 'How many players can participate in a single game?',
                a: 'Sequence supports 2, 3, 4, 6, 8, 9, 10, or 12 players. Players are divided into teams of equal size. With two teams, you need an even number of players. With three teams, the player count must be divisible by three. This flexibility allows you to play anything from a quick one-on-one match to a large team-based game night.'
            },
            {
                q: 'How do teams work in Sequence?',
                a: 'Players are divided into two or three teams, with each team represented by a different color: green, blue, and red. Team members take alternating turns with members of the opposing teams. All members of a team share the same color chips and work together to build sequences. In two-team games, you need two completed sequences to win. In three-team games, only one sequence is needed.'
            },
            {
                q: 'What are the special Jack cards?',
                a: 'There are two types of special Jacks in Sequence. Two-eyed Jacks, which are the Jack of Clubs and Jack of Diamonds, act as wild cards and can be used to place your chip on any open space on the board. One-eyed Jacks, which are the Jack of Spades and Jack of Hearts, are removal cards that let you remove one opponent chip from the board, as long as that chip is not part of an already completed sequence.'
            },
            {
                q: 'What are the corner spaces on the board?',
                a: 'The four corner spaces on the Sequence board are free spaces that count as a chip for every player and every team. When building a sequence that passes through a corner, the corner automatically counts as one of your five chips. This means you only need four additional chips to complete a corner sequence instead of the usual five, making corner positions highly strategic.'
            },
            {
                q: 'What happens if both spaces for a card are already taken?',
                a: 'When both spaces on the board that correspond to a card in your hand are already occupied by chips, that card is called a dead card. You cannot play a dead card to place a chip, but you can declare it as dead on your turn, discard it, and draw a replacement card before playing your normal turn. You can only exchange one dead card per turn. To minimize dead cards, pay attention to which board spaces are being claimed and plan your card plays accordingly.'
            },
            {
                q: 'How many sequences do I need to win?',
                a: 'The number of sequences needed to win depends on the number of teams playing. In a two-player or two-team game, the first side to complete two sequences wins. In a three-team game, only one sequence is needed to win. A sequence consists of five chips in a connected row, either horizontally, vertically, or diagonally.'
            },
        ]
    },
    {
        category: 'Features and Technical',
        questions: [
            {
                q: 'Can I play on my mobile phone?',
                a: 'Currently, Play Sequence is designed for desktop and laptop browsers with landscape orientation. The game board requires a wider screen to display all 100 board spaces and your card hand comfortably. We recommend using a computer or tablet in landscape mode for the best gaming experience. Mobile support may be added in a future update.'
            },
            {
                q: 'How does the video call feature work?',
                a: 'The video call feature uses WebRTC technology to establish direct peer-to-peer connections between players. When you click "Start Call," your browser will ask for permission to use your camera and microphone. Once granted, you can see and hear other players who have also joined the call. The video and audio are transmitted directly between players without passing through a central server, providing low latency and privacy.'
            },
            {
                q: 'How does the in-game chat work?',
                a: 'The chat feature is accessible through the chat icon in the bottom-right corner of the game screen. Click the icon to open the chat panel, where you can type messages that are visible to all players in the game. New message notifications appear as a badge on the chat icon so you never miss a message during gameplay.'
            },
            {
                q: 'Can I customize the appearance of my game?',
                a: 'Yes, you can change the background color of your game board using the "Change Background Color" button in the toolbar at the top of the game screen. This opens a color picker that lets you select any color you prefer, allowing you to personalize your gaming experience and match your mood or team spirit.'
            },
            {
                q: 'What happens if a player disconnects during a game?',
                a: 'If a player disconnects, the game will show their last seen time so other players know they have left. The game can continue, but the disconnected player will miss their turns until they reconnect by reopening the game link. The game state is maintained on the server, so reconnecting players will see the current board state when they return.'
            },
            {
                q: 'How do I report a bug or give feedback?',
                a: 'You can report bugs or provide feedback using the "Feedback" button in the game toolbar at the top of the screen. This opens a form where you can select the type of report, such as a bug report or feature request, and provide a detailed description. Your feedback helps us improve the platform for all players. You can also reach us through our Contact page.'
            },
        ]
    },
];

const FAQ = () => {
    return (
        <Box>
            <Helmet>
                <title>Frequently Asked Questions - Sequence Game FAQ | Play Sequence Online</title>
                <meta name="description" content="Find answers to common questions about playing Sequence online. Learn about game rules, features, team play, technical requirements, and more." />
                <meta property="og:title" content="Sequence Game FAQ - Frequently Asked Questions" />
                <meta property="og:description" content="Get answers to common questions about playing Sequence online, including rules, features, and troubleshooting." />
            </Helmet>

            {/* Hero Section */}
            <Box sx={{ backgroundColor: '#282c34', color: '#fff', py: 6, textAlign: 'center' }}>
                <Container maxWidth="md">
                    <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
                        Frequently Asked Questions
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Everything you need to know about playing Sequence online
                    </Typography>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ py: 6 }}>
                {faqs.map((section) => (
                    <Box key={section.category} sx={{ mb: 5 }}>
                        <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3, color: '#1a1d23' }}>
                            {section.category}
                        </Typography>
                        {section.questions.map((faq, index) => (
                            <Accordion
                                key={index}
                                sx={{
                                    mb: 1,
                                    '&:before': { display: 'none' },
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                    borderRadius: '8px !important',
                                    overflow: 'hidden',
                                }}
                                disableGutters
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        backgroundColor: '#fff',
                                        '&:hover': { backgroundColor: '#fafafa' },
                                        minHeight: 56,
                                    }}
                                >
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1a1d23' }}>
                                        {faq.q}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ backgroundColor: '#fff', borderTop: '1px solid #eee' }}>
                                    <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.8 }}>
                                        {faq.a}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                ))}

                {/* CTA */}
                <Paper sx={{ p: 4, textAlign: 'center', mt: 4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1a1d23' }}>
                        Still Have Questions?
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#666', mb: 3 }}>
                        Feel free to reach out to us through our contact page or use the in-game feedback feature.
                    </Typography>
                    <Button component={Link} to="/contact" variant="outlined" sx={{ mr: 2, borderColor: '#3f51b5', color: '#3f51b5' }}>
                        Contact Us
                    </Button>
                    <Button component={Link} to="/" variant="contained" sx={{ backgroundColor: '#3f51b5', '&:hover': { backgroundColor: '#303f9f' } }}>
                        Start Playing
                    </Button>
                </Paper>
            </Container>
        </Box>
    );
};

export default FAQ;
